/* eslint-disable guard-for-in */
/* eslint-disable @typescript-eslint/prefer-for-of */
import { Inject, Injectable } from '@angular/core';
import { ENV, IEnvironment } from 'env/config';
import { oneLine } from '@angular-devkit/core/src/utils/literals';
import { Observable, zip } from 'rxjs';

import { Repository } from './repository';
import { DataService } from '../http/dataService';
import { Filters, InsightsBodyRequest, Result } from '../interfaces/helpers';
import { Service } from '../entity';
import {
  FeatureCollection,
  FILTER_OPERATORS,
  FILTER_OPERATORS_BETWEEN,
  IFeature,
} from '../models/feature.model';
import { map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { commonConstants } from '../constants/common.constants';
import GeoJSON from 'ol/format/GeoJSON';
import GeoToGML from 'geojson-to-gml-2/geomToGml-2.1.2';

@Service({
  api: 'telus/wfs',
  model: FeatureCollection,
})
@Injectable({ providedIn: 'root' })
export class FeatureCollectionService extends Repository<IFeature> {
  private header;

  constructor(@Inject(ENV) env: IEnvironment, dataService: DataService) {
    super({
      client: dataService,
      url: `${env.api.backend.d2cGeo.endpoint}`,
    });

    this.header = env.api.headers.d2cGeo;
  }

  getAll(queryParams: any): Observable<Result<IFeature>> {
    return super.getAll(queryParams, this.header);
  }

  getHouseholdsByPolygon(
    queryParams: any,
    polygon: any
  ): Observable<Result<IFeature>> {
    return this.getHouseholdsByPolygonWithFilters(queryParams, polygon, null);
  }

  getHouseholdsByPolygonWithFilters(
    queryParams: any,
    polygon: any,
    filters: Array<any>
  ): Observable<Result<IFeature>> {
    return zip(
      super.post(this.xmlQuery(polygon), this.header, queryParams),
      super.post(
        this.xmlQueryWithFilters(polygon, filters),
        this.header,
        queryParams
      )
    ).pipe(
      map(
        (value) =>
          ({
            meta: {
              currentPage: 0,
              itemCount: value[1].numberReturned,
              itemsPerPage: value[1].numberReturned,
              totalItems: value[1].numberMatched,
              totalPages: 1,
              totalItemsNoFiltered: value[0].numberReturned,
            },
            links: {
              first: null,
              last: null,
              next: null,
              previous: null,
            },
            source: 'geoserver',
            items: value[1].features,
          } as Result<IFeature>)
      )
    );
  }

  formatItemsId(items: any[]): InsightsBodyRequest {
    return {
      addressIds: items.map((household) =>
        household['id'].split('.', 2)[1].toString()
      ),
    };
  }

  private xmlQuery(pol: any) {
    return this.xmlQueryWithFilters(pol, null);
  }

  private xmlQueryWithFilters(pol: any, filters: Array<any>) {
    if (!pol.getCoordinates()[0]) {
      return '';
    }
    let filtersXml = '';
    if (filters) {
      filtersXml = this.makeFilters(filters);
    }
    const writer = new GeoJSON();
    const geoJsonStr = writer.writeGeometryObject(pol);
    const text = GeoToGML.geomToGml(geoJsonStr);
    return oneLine`<?xml version="1.0"?>
      <wfs:GetFeature xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:schemaLocation="http://www.opengis.net/wfs"
        xmlns:gml="http://www.opengis.net/gml" xmlns:wfs="http://www.opengis.net/wfs"
        xmlns:ogc="http://www.opengis.net/ogc" service="WFS" version="1.0.0">
        <wfs:Query typeName="telus:address_extension_gs_view">
          <ogc:Filter>
            <And>
              <ogc:Within>
                <ogc:PropertyName>address_geom</ogc:PropertyName>
                ${text}
              </ogc:Within>
              ${filtersXml}
            </And>
          </ogc:Filter>
        </wfs:Query>
      </wfs:GetFeature>`;
  }

  private makeFilters(filtersList: Array<any>): string {
    let filterString = '';
    if (filtersList) {
      for (const groupsKey in filtersList) {
        let needNullValue = '';
        const filters = filtersList[groupsKey];
        filterString = filterString.concat(`<Or>`);
        for (const filter of filters) {
          if (filter.subtype) needNullValue = '';
          filterString = filterString.concat(`<And>`);
          const val = this.transformVal(filter.operator, filter.value);
          // filter.value === null → val.val == null → getOperator(null) →
          // FILTER_OPERATORS[null] = 'PropertyIsNull' → ogc:PropertyIsNull.
          // This is the mechanism for IS NULL on the primary field (e.g. drop_status,
          // copper_port_availability). always_null is unrelated — see below.
          const operator = this.getOperator(
            val.val == null ? null : filter.operator
          );
          if (operator.operator === 'PropertyIsNull') {
            filterString = filterString.concat(`<ogc:PropertyIsNull>
                    <ogc:PropertyName>${groupsKey}</ogc:PropertyName>
                  </ogc:PropertyIsNull>`);
          } else {
            filterString = filterString.concat(`<ogc:${operator.operator}>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:Function name="jsonPointer">'
                        : ''
                    }
                    <ogc:PropertyName>${groupsKey}</ogc:PropertyName>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:literal>/' +
                          filter.subField +
                          '</ogc:literal></ogc:Function>'
                        : ''
                    }
                   <ogc:literal>${val.val}</ogc:literal>
                 </ogc:${operator.operator}>`);
          }
          if (filter.coid) {
            filterString = filterString.concat(`<ogc:PropertyIsEqualTo>
                    <ogc:PropertyName>coid</ogc:PropertyName>
                    <ogc:literal>${filter.coid}</ogc:literal>
                  </ogc:PropertyIsEqualTo>`);
          }
          if (val.between != null && operator.operatorBetween != null) {
            filterString = filterString.concat(`<ogc:${
              operator.operatorBetween
            }>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:Function name="jsonPointer">'
                        : ''
                    }
                    <ogc:PropertyName>${groupsKey}</ogc:PropertyName>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:literal>/' +
                          filter.subField +
                          '</ogc:literal></ogc:Function>'
                        : ''
                    }
                   <ogc:literal>${val.between}</ogc:literal>
                 </ogc:${operator.operatorBetween}>`);
          }
          // always_null adds a SECONDARY ogc:PropertyIsNull on a joined/related field,
          // not on the primary field. Used by partner_status combined queries where
          // premise_type = 'MDU' AND partner_status IS NULL must appear together.
          // Do NOT use always_null to achieve IS NULL on the primary field — set
          // filter.value = null instead (handled above via val.val == null).
          if (
            (filter.always_null && needNullValue === '') ||
            val.addSameFilter
          ) {
            const operatorNull = this.getOperator(
              filter.always_null ? null : filter.subtype ? '=' : '>'
            );
            const literal = val.addSameFilter
              ? `<ogc:literal>${
                  filter.subtype ? filter.subField : "'0'"
                }</ogc:literal>`
              : '';
            const property = val.addSameFilter ? filter.join : groupsKey;
            if (operatorNull.operator === 'PropertyIsNull') {
              needNullValue = needNullValue.concat(`<ogc:PropertyIsNull>
                    <ogc:PropertyName>${property}</ogc:PropertyName>
                  </ogc:PropertyIsNull>`);
            } else {
              needNullValue = needNullValue.concat(`<ogc:${
                operatorNull.operator
              }>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:Function name="jsonPointer">'
                        : ''
                    }
                    <ogc:PropertyName>${property}</ogc:PropertyName>
                    ${
                      filter.type === 'JSON'
                        ? '<ogc:literal>/' +
                          filter.subField +
                          '</ogc:literal></ogc:Function>'
                        : ''
                    }
                    ${literal}
                 </ogc:${operatorNull.operator}>`);
            }
          }
          filterString = filterString.concat(needNullValue).concat(`</And>`);
        }
        filterString = filterString.concat(`</Or>`);
      }
    }
    return filterString;
  }

  private getOperator(op: string): any {
    let operator = 'PropertyIsEqualTo';
    let operatorBetween = null;
    if (FILTER_OPERATORS[op]) {
      operator = FILTER_OPERATORS[op];
    }
    if (FILTER_OPERATORS_BETWEEN[op]) {
      operatorBetween = FILTER_OPERATORS_BETWEEN[op];
    }
    return { operator, operatorBetween };
  }

  private transformVal(op: string, val: any): any {
    const date = new Date();
    let between = null;
    let addSameFilter = true;
    const datepipe: DatePipe = new DatePipe('en-CA');
    switch (op) {
      case 'NT':
        date.setDate(date.getDate() + val * -1);
        val = datepipe.transform(date, commonConstants.DATE_FORMATS.yyyy_MM_DD);
        between = datepipe.transform(
          new Date(),
          commonConstants.DATE_FORMATS.yyyy_MM_DD
        );
        break;
      case 'IC':
      case 'MTM':
        date.setDate(date.getDate() + val);
        val = datepipe.transform(date, commonConstants.DATE_FORMATS.yyyy_MM_DD);
        break;
      case 'NC':
        val = null;
        break;
      case 'MDU':
        val = 'MDU';
        break;
      default:
        addSameFilter = false;
        break;
    }
    return { val, between, addSameFilter };
  }

  /**
   * @returns A tile loader function compatible with OpenLayers ImageWMS sources
   */
  createTileLoader() {
    const dataService = this.dataService;
    const header = this.header;

    return function tileLoader(tile: any, src: string) {
      dataService
        .get(
          src,
          {},
          {
            env: header.env,
          },
          false,
          'blob'
        )
        .subscribe({
          next: (blob: Blob) => {
            const urlCreator = window.URL || window.webkitURL;
            const imageUrl = urlCreator.createObjectURL(blob);
            tile.getImage().src = imageUrl;
          },
          error: (error) => {
            console.error('Tile loading failed:', error);
          },
        });
    };
  }
}
